import { Menu, MenuItem, ListItemIcon, ListItemText, styled } from "@mui/material";
import { SwapHoriz, Delete, Block, LockOpen } from "@mui/icons-material";
import { toast } from "react-toastify";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { Can } from "../Can";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModal from "../TransferTicketModal";

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
	padding: theme.spacing(1, 2),
	"&:hover": {
		backgroundColor: theme.palette.grey[100],
	},
}));

const MenuItemIcon = styled(ListItemIcon)(({ theme }) => ({
	minWidth: "40px",
	color: theme.palette.primary.main,
}));

const TicketOptionsMenu = ({ ticket, menuOpen, handleClose, anchorEl }) => {
	const { t } = useTranslation();
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	// CC_BLOQUEIO_CONTATO (2026-11-03): bloquear/desbloquear o contato direto do atendimento.
	// Canal oficial (cloudapi) -> rotas do ticket (Gestor + Meta); demais canais -> rotas de contato.
	const isCloud = ticket?.whatsapp?.type === "cloudapi";
	const [blocked, setBlocked] = useState(false);
	const [blockConfirmOpen, setBlockConfirmOpen] = useState(false);

	useEffect(() => {
		if (!menuOpen || !ticket?.id || ticket?.contact?.isGroup) return;
		const url = isCloud
			? `/tickets/${ticket.id}/block-status`
			: `/contacts/${ticket.contactId}/block-status`;
		api.get(url).then(({ data }) => {
			if (isMounted.current) setBlocked(Boolean(data?.blocked ?? data?.isBlocked));
		}).catch(() => {});
	}, [menuOpen, ticket?.id]);

	const handleToggleBlock = async () => {
		try {
			const action = blocked ? "unblock" : "block";
			let data;
			if (isCloud) {
				({ data } = await api.post(`/tickets/${ticket.id}/${action}-contact`));
			} else {
				({ data } = await api.post(`/contacts/${ticket.contactId}/${action}`));
			}
			setBlocked(!blocked);
			if (action === "block") {
				if (isCloud && data && data.metaOk === false) {
					toast.warn("Contato bloqueado aqui (a IA não responde mais). A Meta não aceitou o bloqueio porque ele não falou nas últimas 24h.");
				} else {
					toast.success("Contato bloqueado.");
				}
				await api.put(`/tickets/${ticket.id}`, { status: "closed", userId: user?.id || null });
				window.dispatchEvent(new CustomEvent("pressticket:ticketClosed", { detail: { ticketId: ticket.id } }));
			} else {
				toast.success("Contato desbloqueado.");
			}
		} catch (err) {
			toastError(err);
		}
	};

	const handleDeleteTicket = async () => {
		try {
			await api.delete(`/tickets/${ticket.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenConfirmationModal = e => {
		setConfirmationOpen(true);
		handleClose();
	};

	const handleOpenTransferModal = e => {
		setTransferTicketModalOpen(true);
		handleClose();
	};

	const handleCloseTransferTicketModal = () => {
		if (isMounted.current) {
			setTransferTicketModalOpen(false);
		}
	};

	return (
		<>
			<Menu
				id="menu-appbar"
				anchorEl={anchorEl}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				keepMounted
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				open={menuOpen}
				onClose={handleClose}
			>
				<StyledMenuItem onClick={handleOpenTransferModal}>
					<MenuItemIcon>
						<SwapHoriz />
					</MenuItemIcon>
					<ListItemText primary={t("ticketOptionsMenu.transfer")} />
				</StyledMenuItem>
				{!ticket?.contact?.isGroup && (
					<StyledMenuItem onClick={() => { setBlockConfirmOpen(true); handleClose(); }}>
						<MenuItemIcon>
							{blocked ? <LockOpen /> : <Block />}
						</MenuItemIcon>
						<ListItemText primary={blocked ? "Desbloquear contato" : "Bloquear contato"} />
					</StyledMenuItem>
				)}
				<Can
					role={user.profile}
					perform="ticket-options:deleteTicket"
					yes={() => (
						<StyledMenuItem onClick={handleOpenConfirmationModal}>
							<MenuItemIcon>
								<Delete />
							</MenuItemIcon>
							<ListItemText primary={t("ticketOptionsMenu.delete")} />
						</StyledMenuItem>
					)}
				/>
			</Menu>
			<ConfirmationModal
				title={`${t("ticketOptionsMenu.confirmationModal.title")}${ticket.id
					} ${t("ticketOptionsMenu.confirmationModal.titleFrom")} ${ticket.contact.name
					}?`}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleDeleteTicket}
			>
				{t("ticketOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>
			<ConfirmationModal
				title={`${blocked ? "Desbloquear" : "Bloquear"} ${ticket?.contact?.name || "contato"}?`}
				open={blockConfirmOpen}
				onClose={setBlockConfirmOpen}
				onConfirm={handleToggleBlock}
			>
				{blocked
					? "O contato poderá voltar a falar com você e receber respostas."
					: "A IA e as cadências param de falar com este contato, as mensagens dele deixam de aparecer aqui e a conversa será encerrada."}
			</ConfirmationModal>
			<TransferTicketModal
				modalOpen={transferTicketModalOpen}
				onClose={handleCloseTransferTicketModal}
				ticketid={ticket.id}
			/>
		</>
	);
};

export default TicketOptionsMenu;
